import Codes from '../../socket/codes';
import { BehaviorSubject, Subject } from 'rxjs';
import UniqueId from '@base/utils/uniqueId';
import { getRequiredEnv, t } from '@base/utils/helpers';
import Color from '@base/features/game/model/color';
import OpponentIdleActionsDialogImpl from './opponentIdleActionsDialog';
import { ClockImpl } from './clock';
import {
  ITable,
  BaseGameState,
  Clock,
  GameButton,
  OpponentIdleActionsDialog,
  IGameService,
  ITableRepository,
} from './interfaces';
import { StringColor, TableSides } from './types';
import { MessagesService } from '@base/features/chat/messagesService';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { BasePreferences } from '@base/features/preferences/model/types';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import SoundProvider from './soundProvider';
import { ISocketClient } from '@base/features/socket/interfaces';
import LobbyService from '@base/features/lobby/model/lobbyService';

const GameAction = Codes.GameActions;

export default abstract class BaseGameStateImpl<P extends BasePreferences, T extends ITable>
  implements BaseGameState {
  onTurnChanged = new Subject<TableSides>();
  onStatusMessagesChanged = new BehaviorSubject('');
  opponentIdleActionsDialog: OpponentIdleActionsDialog;
  onGameStarted = new Subject<boolean>();
  onGameFinished = new Subject<boolean>();
  onRoundStarted = new Subject<boolean>();
  onRoundFinished = new Subject<boolean>();
  onScoreChanged = new Subject<boolean>();
  onPlayersCountChanged = new BehaviorSubject<number>(0);
  onMoveTimeChanged = new Subject<boolean>();
  autoRejectAborts = false;
  // client: ISocketClient;
  currentTurn?: TableSides;
  uniqueId: string;
  me: IPlayer;
  clock: Clock;
  mySide: TableSides;
  myOp?: TableSides;
  maxPoints: number;
  oldStatusMessage = '';
  restoreTimeout?: NodeJS.Timeout = undefined;
  disconnectTime?: { [pid: string]: Date };
  whitePlayer?: IPlayer;
  blackPlayer?: IPlayer;

  gameInProgress = new BehaviorSubject(false);
  roundInProgress = new BehaviorSubject(false);
  waitingForOpponentToStart = false;
  onGameButtonsChanged = new BehaviorSubject<GameButton[]>([]);

  score = {
    SOUTH: 0,
    NORTH: 0,
  };
  finalScore = [0, 0];
  sendMoveTime = 0;
  sendMoveString = '';
  lastMoveDelay = 0;
  averageMoveDelay = 0;

  initScore() {
    this.score = {
      NORTH: 0,
      SOUTH: 0,
    };
  }

  constructor(
    public table: T,
    private tablesRepository: ITableRepository,
    protected playersOnlineRepository: IPlayersOnlineRepository,
    public lobbyService: LobbyService,
    protected messagesService: MessagesService<P, T>,
    protected modalProvider: IModalProvider,
    protected gameService: IGameService,
    private soundProvider: SoundProvider<P>,
    protected socketClient: ISocketClient,
  ) {
    this.uniqueId = UniqueId.getShortId();

    if (!this.playersOnlineRepository.me) throw new Error('Me is not initialized');
    this.me = this.playersOnlineRepository.me;
    this.mySide = this.me.getSeat(table) === 1 ? TableSides.NORTH : TableSides.SOUTH;
    this.myOp = this.reverseSide(this.mySide);
    this.opponentIdleActionsDialog = new OpponentIdleActionsDialogImpl(
      table,
      playersOnlineRepository,
      lobbyService,
      gameService.gameData.reconnectionTime,
      this.socketClient,
    );

    this.clock = new ClockImpl(this, table.timePerMove, this.gameService);

    this.maxPoints = table.maxPoints;
    this.onPlayersCountChanged.next(this.table.getCountOfPlayers());
    this.initScore();
  }

  checkSendMoveTime(moveString: string) {
    this.sendMoveString = moveString;
    this.sendMoveTime = new Date().getTime();
  }

  checkReceiveMoveTime(moveString: string) {
    if (this.sendMoveString !== moveString) return;

    this.lastMoveDelay = new Date().getTime() - this.sendMoveTime;
    if (this.averageMoveDelay === 0) {
      this.averageMoveDelay = this.lastMoveDelay;
    } else {
      this.averageMoveDelay = Math.floor((this.averageMoveDelay + this.lastMoveDelay) / 2);
    }

    // this.onMoveTimeChanged.next([this]);
    this.onMoveTimeChanged.next(true);
  }

  getLogPrefix() {
    return '[GS' + this.uniqueId + '] ';
  }

  getScore(color: Color) {
    let side;
    if (!this.mySide) {
      side = this.getColorForSide(TableSides.SOUTH) === color ? TableSides.SOUTH : TableSides.NORTH;
    } else if (color === this.getMyColor()) {
      side = this.mySide;
    } else {
      side = this.mySide === TableSides.SOUTH ? TableSides.NORTH : TableSides.SOUTH;
    }
    return this.score[side];
  }

  canBanPlayer(player: IPlayer) {
    if (!player) return false;

    const owner = this.table.getOwner();
    const imOwner = Boolean(owner && owner.id === this.me.id);
    const canBan = Boolean(
      imOwner && this.imPlaying() && !this.isGameInProgress() && this.table.isAllPlaying()
    );

    return Boolean(canBan && player.id !== this.me.id);
  }

  sideFromString(str: string) {
    if (str === 'SOUTH') return TableSides.SOUTH;
    else return TableSides.NORTH;
  }

  reverseSide(side: TableSides): TableSides {
    return side === TableSides.NORTH ? TableSides.SOUTH : TableSides.NORTH;
  }

  getTable(): ITable {
    return this.table;
  }

  getColorStringForSide(side: TableSides) {
    return this.getColorForSide(side);
  }

  getMyColor() {
    if (!this.mySide) throw new Error('No side for me');
    return this.getColorForSide(this.mySide);
  }

  getReverseColor(color: StringColor) {
    return color === 'WHITE' ? 'WHITE' : 'BLACK';
  }

  getCurrentTurn() {
    if (this.currentTurn) return this.getColorForSide(this.currentTurn);
  }

  getPlayer(color: Color) {
    let side;
    if (!this.mySide) {
      side = this.getColorForSide(TableSides.SOUTH) === color ? TableSides.SOUTH : TableSides.NORTH;
    } else if (color === this.getMyColor()) {
      side = this.mySide;
    } else {
      side = this.mySide === TableSides.SOUTH ? TableSides.NORTH : TableSides.SOUTH;
    }
    return this.table.getPlayerBySeat(side === TableSides.SOUTH ? 0 : 1);
  }

  getClock() {
    return this.clock;
  }

  setCurrentTurn(side: TableSides, restartClock?: boolean) {
    if (restartClock === undefined) restartClock = true;
    //Only for backgammon
    if (getRequiredEnv('NX_GAME_ID') === '5') {
      if (this.currentTurn === side) return;
    }

    this.currentTurn = side;
    this.onTurnChanged.next(this.currentTurn);
    this.clock?.startClock(this.getColorStringForSide(this.currentTurn), restartClock);
  }

  getReversedTurn() {
    return this.currentTurn === TableSides.NORTH ? TableSides.SOUTH : TableSides.NORTH;
  }

  getStatusMessage() {
    return this.onStatusMessagesChanged.value;
  }

  setStatusMessage(statusMessage: string, restoreMessageTimeout?: number) {
    if (this.restoreTimeout) {
      clearTimeout(this.restoreTimeout);
      this.restoreTimeout = undefined;
    }

    this.oldStatusMessage = this.getStatusMessage();
    this.onStatusMessagesChanged.next(statusMessage);

    if (restoreMessageTimeout) {
      this.restoreTimeout = setTimeout(() => {
        this.onStatusMessagesChanged.next(this.oldStatusMessage);
      }, restoreMessageTimeout);
    }
  }

  imWatching() {
    return !this.me.isPlayingTable(this.getTable());
  }

  imPlaying() {
    const table = this.getTable();
    if (table && this.tablesRepository.getTable(table.tid)) {
      return this.me.isPlayingTable(table);
    }

    return false;
  }

  isMyTurn(): boolean {
    if (this.imWatching()) {
      return false;
    }

    return this.currentTurn === this.mySide;
  }

  isGameInProgress(): boolean {
    return this.gameInProgress.value;
  }

  setGameInProgress(inProgress: boolean) {
    if (this.isGameInProgress() === inProgress) return;
    const id = this.me.id;
    this.gameInProgress.next(inProgress);

    if (inProgress) {
      this.onGameButtonsChanged.next([]);
      if (this.imWatching()) {
        this.setStatusMessage('');
      } else {
        this.setStatusMessage(t('game_started'));
      }
      this.onGameStarted.next(true);
    } else {
      if (this.imPlaying()) {
        this.setStatusMessage(t('game_ended') + '. ' + t('press_new_game'));
        this.onGameButtonsChanged.next([GameButton.NEW_GAME]);
      } else {
        this.setStatusMessage(t('game_ended'));
        this.onGameButtonsChanged.next([]);
      }

      this.playersOnlineRepository.sendFingerPid(id!);
      this.hideReconnectionDialog();
      this.onGameFinished.next(true);
    }
  }

  isRoundInProgress() {
    return this.roundInProgress.value;
  }

  handlerAcceptAbort(player: IPlayer) {
    this.gameService.sendPlay(Codes.PlayCommand.ABORT_OK, player.id!, this.me.getTable()?.tid);
  }

  handlerRejectAbort(player: IPlayer) {
    this.gameService.sendPlay(Codes.PlayCommand.ACTION_REFUSED, player.id!, this.me.getTable()?.tid);
  }

  handlerAbortDontAsk(dontAsk: boolean) {
    this.autoRejectAborts = Boolean(dontAsk);
  }

  onActionReceived(action: typeof GameAction) {
    const player = action.player;
    const sender = action.player.getSeat(action.table) === 0 ? TableSides.SOUTH : TableSides.NORTH;

    if (player.id !== this.me.id) {
      if (action.cmd !== GameAction.OFFER_ABORT && action.cmd !== GameAction.REFUSED) {
        this.hideReconnectionDialog();
      }
    }

    switch (action.cmd) {
      case GameAction.START:
        this.setRoundInProgress(true);
        this.setGameInProgress(true);
        this.setStatusMessage(t('game_started'));
        this.setCurrentTurn(sender, true);
        this.autoRejectAborts = false;
        this.waitingForOpponentToStart = false;
        break;
      case GameAction.START_PRESSED:
        if (player.getPid() === this.me.getPid()) {
          this.waitingForOpponentToStart = true;
          this.setStatusMessage(t('waiting_for_opponent_to_start'));
        } else if (this.waitingForOpponentToStart) {
          this.waitingForOpponentToStart = false;
          this.gameService.sendPlay(Codes.PlayCommand.START, this.me.getPid(), this.me.getTable()?.tid);
        }

        break;
      case GameAction.CONTINUE:
        if (player.id === this.me.id) {
          this.setStatusMessage(t('waiting_for_opponent_to_press_continue'));
        }
        break;
      case GameAction.OFFER_RESTART:
        if (player.getPid() !== this.me.getPid()) {
          this.showNewGameDialog(player);
        }
        break;
      case GameAction.OFFER_ABORT:
        if (player.id === this.me.id) break;
        if (this.autoRejectAborts) {
          this.gameService.sendPlay(Codes.PlayCommand.ACTION_REFUSED, player.id, this.me.getTable()?.tid);
        } else {
          this.modalProvider.show({
            title: t('opponents_request'),
            body: t('arg_asks_abort_current_game', player.getName()),
            buttons: [
              {
                title: t('no'),
                onClick: () => {
                  this.handlerRejectAbort(player);
                  this.modalProvider.hide();
                },
              },
              {
                title: t('yes'),
                onClick: () => {
                  this.handlerAcceptAbort(player);
                  this.modalProvider.hide();
                },
              },
            ],
          });
        }
        break;
      case GameAction.REFUSED:
        this.setStatusMessage(t('request_declined_by_opponent'));
        setTimeout(this.refreshStatusMessage.bind(this), 3000);
        break;
      //@ts-ignore
      case GameAction.REVIEW:
        this.hideReconnectionDialog();
        this.messagesService.createChatMessage({
          type: Codes.MessageTypes.TABLE,
          text: t('game_annuled'),
          tid: this.getTable().tid,
        });
      // eslint-disable-next-line no-fallthrough
      case GameAction.BREAK:
      case GameAction.END:
        this.hideReconnectionDialog();
        this.setRoundInProgress(false);
        this.setGameInProgress(false);
        this.autoRejectAborts = false;
        this.clock?.stopClock();
        this.saveFinalScore();
        this.initScore();
        break;
    }
  }

  get sound() {
    return this.soundProvider;
  }

  private showReconnectionDialog(playMode: boolean, controls: boolean) {
    this.hideReconnectionDialog();
    this.opponentIdleActionsDialog.show(playMode, controls);
  }

  hideReconnectionDialog() {
    if (!this.opponentIdleActionsDialog.visible.value) return;
    this.opponentIdleActionsDialog.hide();
    this.sound.play(this.sound.codes.INFO, this.sound.categories.OPP_MOVE);
  }

  updateReconnectionDialogTime() {
    const oldestDisonnect = this.getOldestDisconnectTime();
    const now = new Date();
    const elapsedMs = oldestDisonnect ? now.getTime() - oldestDisonnect.getTime() : 0;
    this.opponentIdleActionsDialog.setTime(Math.round(elapsedMs / 1000));
  }

  getOldestDisconnectTime() {
    this.disconnectTime = this.disconnectTime || {};
    let oldestTime = undefined;
    for (const pid in this.disconnectTime) {
      if (this.disconnectTime.hasOwnProperty(pid)) {
        if (!oldestTime || oldestTime.getTime() > this.disconnectTime[pid].getTime()) {
          oldestTime = this.disconnectTime[pid];
        }
      }
    }
    return oldestTime;
  }

  recordDisconnectTime(pid: number) {
    this.disconnectTime = this.disconnectTime || {};
    this.disconnectTime[pid] = new Date();
  }

  eraseDisconnectTime(pid: number) {
    this.disconnectTime = this.disconnectTime || {};
    if (this.getTable().getMaxPlayersCount() === 2) {
      this.disconnectTime = {};
    } else {
      delete this.disconnectTime[pid];
    }
  }

  getDisconnectedPlayersCount() {
    this.disconnectTime = this.disconnectTime || {};
    let count = 0;
    for (const pid in this.disconnectTime) {
      if (this.disconnectTime.hasOwnProperty(pid)) {
        for (let i = 0; i < this.table.getMaxPlayersCount(); ++i) {
          const player = this.table.getPlayerBySeat(i);
          if (player && player.getPid() === Number(pid)) {
            count++;
          }
        }
      }
    }
    return count;
  }

  returnToTable() {
    //TODO: return to table
  }

  onReconnectionReceieved(command: any, player: IPlayer, par: number) {
    switch (command) {
      case Codes.Commands.POSTPONE: // Информирует о потере соединения с другим игроком
        this.recordDisconnectTime(player.getPid());
        if (this.imPlaying()) {
          if (!this.opponentIdleActionsDialog.visible.value) {
            this.returnToTable();
            this.showReconnectionDialog(false, false);

            const soundProvider = this.sound;
            soundProvider.play(soundProvider.codes.INFO, soundProvider.categories.OPP_MOVE);
          }
        } else {
          this.messagesService.createChatMessage({
            type: Codes.MessageTypes.TABLE,
            text: player.getName() + t('_has_lost_connection_to_the_game_server_'),
            tid: this.getTable().tid,
          });
        }
        this.clock.stopClock();
        break;
      case Codes.Commands.R_ENABLE:
        if (!this.imPlaying) break;
        if (!this.opponentIdleActionsDialog.visible.value) break;
        this.opponentIdleActionsDialog.end();
        this.sound.play(this.sound.codes.INFO, this.sound.categories.OPP_MOVE);
        break;
      case Codes.Commands.R_WAIT:
        if (!this.imPlaying) break;
        if (!this.opponentIdleActionsDialog.visible.value) break;
        this.opponentIdleActionsDialog.wait();
        this.sound.play(this.sound.codes.INFO, this.sound.categories.OPP_MOVE);
        break;
      case Codes.Commands.R_STOP: {
        // Соединение восстановлено. Закрытие диалога и продолжение игры
        this.eraseDisconnectTime(player.getPid());
        if (this.imPlaying()) {
          if (this.getDisconnectedPlayersCount() === 0) {
            this.hideReconnectionDialog();
          } else {
            this.updateReconnectionDialogTime();
          }
          this.messagesService.createChatMessage({
            type: Codes.MessageTypes.TABLE,
            text: t('your_opponent_has_reconnected_to_the_game'),
            tid: this.getTable().tid,
          });
        } else {
          // С сервера не приходит, невозможно проверить
          this.messagesService.createChatMessage({
            type: Codes.MessageTypes.TABLE,
            text: player.getName() + t('_has_reconnected_to_the_game'),
            tid: this.getTable().tid,
          });
        }
        // let color;
        // if (Game.ID === 9) {
        //   color = undefined;
        // } else {
        // }
        const color = this.getColorStringForSide(this.getCurrentTurnSide());
        if (this.getDisconnectedPlayersCount() === 0) {
          this.clock.startClock(color, false);
        }
        break;
      }
      case Codes.Commands.PM_REQ:
        if (!this.imPlaying) break;
        this.returnToTable();
        this.showReconnectionDialog(true, true);
        this.sound.play(this.sound.codes.INFO, this.sound.categories.OPP_MOVE);
        break;
      case Codes.Commands.PM_WAIT:
        if (!this.imPlaying || !this.currentTurn) break;
        this.hideReconnectionDialog();
        this.clock.startClock(this.getColorStringForSide(this.currentTurn), true);
        this.sound.play(this.sound.codes.INFO, this.sound.categories.OPP_MOVE);
        break;
      case Codes.Commands.PM_INFO: {
        const oppName = player != null ? player.getName() : t('opponent');
        this.setStatusMessage(oppName + ' ' + t('_warned_if_no_moves_made_you_will_control_the_game'));
        break;
      }
    }
  }

  onPlayerJoined(player: IPlayer) {
    if (this.table.isAllPlaying()) {
      if (this.imPlaying()) {
        this.setStatusMessage(t('start_game_press_start'));
        this.onGameButtonsChanged.next([GameButton.START]);
        if (player.getPid() !== this.me.getPid()) {
          this.setStatusMessage(t('player') + ' ' + player.getName() + t('_joined_your_table_'), 2000);
        }
      }
    } else {
      this.setStatusMessage(t('to_invite_select_invite'));
      this.onGameButtonsChanged.next([GameButton.INVITE]);
    }
    this.onPlayersCountChanged.next(this.table.getCountOfPlayers());
  }

  onPlayerLeft(player: IPlayer) {
    this.setRoundInProgress(false);
    this.setGameInProgress(false);
    //to prevent auto resending start when other opponent connects
    this.waitingForOpponentToStart = false;
    if (this.imWatching()) {
      this.setStatusMessage('');
      this.onGameButtonsChanged.next([]);
    } else {
      this.setStatusMessage(t('opponent_left_table') + ' INVITE');
      this.onGameButtonsChanged.next([GameButton.INVITE]);
    }

    if (this.getWhitePlayer()?.pid === player.pid) {
      this.whitePlayer = undefined;
    }  

    if (this.getBlackPlayer()?.pid === player.pid) {
      this.blackPlayer = undefined;
    }

    this.onPlayersCountChanged.next(this.table.getCountOfPlayers());
  }

  onMoveReceived(move: any) {
    if (move.player && move.player.id !== this.me.id) this.hideReconnectionDialog();
  }

  onShutdown() {
    if (this.clock) {
      this.clock.stopClock();
    }
    this.hideReconnectionDialog();
  }

  startClick() {
    this.setStatusMessage(t('please_wait'));
    this.gameService.sendPlay(Codes.PlayCommand.START, this.me.getPid(), this.me?.getTable()?.tid);
    this.onGameButtonsChanged.next([]);
  }


  newGameClick() {
    this.setStatusMessage(t('start_game_press_start'));
    this.gameService.sendPlay(Codes.PlayCommand.RESTART, this.me.getPid()!, this.me?.getTable()?.tid);
    this.onGameButtonsChanged.next([GameButton.START]);
  }

  showNewGameDialog(player: IPlayer) {
    //if (showingExitDialog) {
    //  shouldShowNewGameDialog = true;
    //  playerWhoProposedNewGame = player;
    //  return;
    //}
    const title = t('opponents_request');
    const message = t('arg_asks_play_one_more_game', player.getName());
    const buttons = [
      {
        title: t('no'),
        onClick: () => {
          if (!this.table.getBlackPlayer() || !this.table.getWhitePlayer()) return;
          this.gameService.sendPlay(Codes.PlayCommand.ACTION_REFUSED, player.id, this.table.tid);
          if (this.me.isMyTable(this.table)) {
            if (this.table.getOwner()?.id === this.me.id) {
              this.lobbyService.sendTableOp(player.id, this.table.tid, Codes.TableOpMode.BAR);
            } else {
              this.lobbyService.leaveTable(this.table);
            }
          }
          this.modalProvider.hide();
        },
      },
      {
        title: t('yes'),
        onClick: () => {
          if (!this.table.getBlackPlayer() || !this.table.getWhitePlayer()) return;
          this.gameService.sendPlay(Codes.PlayCommand.RESTART_OK, player.id, this.table.tid);
          this.modalProvider.hide();
        },
      },
    ];
    this.modalProvider.show({
      title,
      body: message,
      buttons,
    });
  }

  shouldUrgeOtherPlayer() {
    return true;
  }

  onUrgeRecieved() {
    if (this.isMyTurn()) {
      this.messagesService.createChatMessage({
        type: Codes.MessageTypes.TABLE,
        text: t('please_move'),
        tid: this.getTable().tid,
      });
      this.messagesService.createChatMessage({
        type: Codes.MessageTypes.ALL,
        text: t('please_move'),
        tid: this.getTable().tid,
      });
      this.sound.play(this.sound.codes.INFO, this.sound.categories.OPP_MOVE);
    }
  }

  saveFinalScore() {
    this.finalScore = [0, 0];
  }

  getFinalScore() {
    return [0, 0];
  }

  abstract onScoresReceived(scores: any): void;
  abstract getColorForSide(side: TableSides): Color;
  abstract getCurrentTurnSide(): TableSides;
  abstract getWhitePlayer(): IPlayer | undefined;
  abstract getBlackPlayer(): IPlayer | undefined;
  abstract refreshStatusMessage(): void;
  abstract setRoundInProgress(inProgress: boolean): void;
  abstract canMove(): boolean;
  abstract loadGame(status: number, turn: number, times: Array<any>, moves: string): void;
  abstract onDealReceived(deal: any): any;
  abstract continueClick(): void;
}
